import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Organization, OrgSecurityGroup } from '@newgenus/common';
import { Component, Inject, OnInit, inject } from '@angular/core';
import { OrgViewToggleComponent, OrgListViewService } from '../../shared/org-view-toggle';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from '../../../material.module';
import { OrgSecurityGroupDetailsDialogComponent } from '../org-security-group-details-dialog';
import { OrgEntityColorsService } from '../../../org-dashboard/services/org-entity-colors.service';
import { OrgFilterBarComponent, OrgFilterConfig } from '../../shared/org-filter-bar/org-filter-bar.component';
import { OrgSearchBarComponent, PositionSearchResult } from '../../shared/org-search-bar/org-search-bar.component';
import { fuzzyMatch } from '../../shared/org-filter-utils';
import { restrictToKeys } from '../../shared/only-keys';

export interface SecurityGroupsListDialogData {
    organization: Organization;
    positionFilter?: string;
    /** Only these items (a staff member's dashboard tile, ISS-022); absent = all. */
    onlyKeys?: string[];
}

interface SecurityGroupDisplayCard {
    key: string;
    name: string;
    featureCount: number;
    hatCount: number;
    userCount: number;
    isAssigned: boolean;
    positionNames: string[];
    featureNames: string[];
    hatNames: string[];
    userNames: string[];
    isOnPost: boolean;
    orgSecurityGroup: OrgSecurityGroup;
}

@Component({
    selector: 'shared-org-security-groups-list-dialog',
    imports: [
        MaterialModule,
        CommonModule,
        FormsModule,
        OrgFilterBarComponent,
        OrgSearchBarComponent,
        OrgViewToggleComponent,
    ],
    template: `
        <h2 mat-dialog-title class="dialog-header">
            <mat-icon class="mr-2">security</mat-icon>
            Organization Security Groups ({{ filteredSecurityGroups.length }} of {{ allSecurityGroups.length }})

            <!-- Security Group Explorer button -->
            <button
                mat-icon-button
                class="explorer-button"
                (click)="openSecurityGroupExplorer()"
                matTooltip="Open Security Group Explorer - View groups by feature, staff member, or hat"
            >
                <mat-icon>account_tree</mat-icon>
            </button>

            <shared-org-view-toggle></shared-org-view-toggle>
            <button mat-icon-button mat-dialog-close class="close-button">
                <mat-icon>close</mat-icon>
            </button>
        </h2>

        <mat-dialog-content class="dialog-content" [class.ng-list-view]="(listView.mode$ | async) === 'list'">
            <!-- Search Bar -->
            <shared-org-search-bar
                searchLabel="Search security groups"
                searchPlaceholder="Search by name, feature, hat, staff member..."
                [showPositionSearch]="true"
                [positionNames]="allPositionNames"
                [initialPositionTerm]="positionSearchTerm"
                (searchChanged)="onSearchChanged($event)"
                (positionSearchChanged)="onPositionSearchChanged($event)"
            >
            </shared-org-search-bar>

            <!-- Filters -->
            <shared-org-filter-bar
                [filters]="filterConfig"
                [rightFilters]="rightFilterConfig"
                [totalCount]="allSecurityGroups.length"
                activeColor="#f57c00"
                activeDarkColor="#ffb74d"
                (filtersChanged)="onFiltersChanged($event)"
            >
            </shared-org-filter-bar>

            <!-- SG card template (reused in both views) -->
            <ng-template #sgCardTpl let-sg>
                <mat-card class="security-group-card" (dblclick)="onSecurityGroupDoubleClick(sg)">
                    <!-- Top section: icon + name + assigned badge -->
                    <div class="card-identity">
                        <div
                            class="entity-avatar"
                            [ngStyle]="{
                                background:
                                    'linear-gradient(135deg, ' +
                                    entityColors.getColor('securityGroups') +
                                    ' 0%, ' +
                                    entityColors.getColor('securityGroups') +
                                    'cc 100%)',
                            }"
                        >
                            <mat-icon class="entity-icon">security</mat-icon>
                        </div>
                        <div class="identity-text">
                            <span class="entity-name">{{ sg.name | titlecase }}</span>
                            <span class="entity-subtitle" *ngIf="sg.isAssigned">
                                <mat-icon class="assigned-icon">check_circle</mat-icon>
                                Assigned
                            </span>
                        </div>
                    </div>

                    <!-- Footer: badges left, action buttons right -->
                    <div class="card-footer">
                        <div class="badges-row">
                            <span
                                class="badge"
                                matTooltip="Features"
                                [ngStyle]="
                                    sg.featureCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('features'),
                                              color: entityColors.getColor('features'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">star</mat-icon>
                                {{ sg.featureCount }}
                            </span>
                            <span
                                class="badge"
                                matTooltip="Hats using this group"
                                [ngStyle]="
                                    sg.hatCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('hats'),
                                              color: entityColors.getColor('hats'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">school</mat-icon>
                                {{ sg.hatCount }}
                            </span>
                            <span
                                class="badge"
                                matTooltip="Staff (via hats)"
                                [ngStyle]="
                                    sg.userCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('users'),
                                              color: entityColors.getColor('users'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">person</mat-icon>
                                {{ sg.userCount }}
                            </span>
                        </div>
                        <div class="card-actions">
                            <button
                                mat-icon-button
                                matTooltip="View Details"
                                color="primary"
                                (click)="onViewSecurityGroup(sg); $event.stopPropagation()"
                            >
                                <mat-icon>visibility</mat-icon>
                            </button>
                            <button
                                mat-icon-button
                                matTooltip="Edit Security Group"
                                color="accent"
                                (click)="onEditSecurityGroup(sg); $event.stopPropagation()"
                            >
                                <mat-icon>edit</mat-icon>
                            </button>
                        </div>
                    </div>
                </mat-card>
            </ng-template>

            <!-- Grouped by Position view -->
            <ng-container *ngIf="groupByPosition">
                <div *ngFor="let group of positionGroups" class="position-group">
                    <div
                        class="group-header"
                        [ngStyle]="{ 'border-left-color': entityColors.getColor('positions') }"
                        (click)="togglePositionCollapse(group.position)"
                    >
                        <mat-icon class="collapse-icon" [class.collapsed]="collapsedPositions.has(group.position)"
                            >expand_more</mat-icon
                        >
                        <mat-icon [ngStyle]="{ color: entityColors.getColor('positions') }">work</mat-icon>
                        <span class="group-name">{{ group.position }}</span>
                        <span class="group-count">({{ group.securityGroups.length }})</span>
                    </div>
                    <div class="security-groups-grid" *ngIf="!collapsedPositions.has(group.position)">
                        <ng-container *ngFor="let sg of group.securityGroups">
                            <ng-container *ngTemplateOutlet="sgCardTpl; context: { $implicit: sg }"></ng-container>
                        </ng-container>
                    </div>
                </div>
                <div *ngIf="positionGroups.length === 0" class="empty-state">
                    <mat-icon class="empty-icon">account_tree</mat-icon>
                    <p>No org part groups to display</p>
                </div>
            </ng-container>

            <!-- Normal (ungrouped) view -->
            <div class="security-groups-grid" *ngIf="!groupByPosition && filteredSecurityGroups.length > 0">
                <ng-container *ngFor="let sg of filteredSecurityGroups">
                    <ng-container *ngTemplateOutlet="sgCardTpl; context: { $implicit: sg }"></ng-container>
                </ng-container>
            </div>

            <!-- Empty State -->
            <div *ngIf="!groupByPosition && filteredSecurityGroups.length === 0" class="empty-state">
                <mat-icon class="empty-icon">security</mat-icon>
                <p *ngIf="searchTerm">No security groups found matching "{{ searchTerm }}"</p>
                <p *ngIf="!searchTerm && hasActiveFilter">No security groups match the active filters</p>
                <p *ngIf="!searchTerm && !hasActiveFilter">No security groups in this organization</p>
            </div>
        </mat-dialog-content>

        <mat-dialog-actions align="end">
            <button mat-button mat-dialog-close>Close</button>
        </mat-dialog-actions>
    `,
    styles: [
        `
            .dialog-header {
                display: flex;
                align-items: center;
                gap: 8px;
                margin: 0;
                padding: 16px 24px;
                border-bottom: 1px solid rgba(0, 0, 0, 0.12);
            }

            .explorer-button {
                color: var(--entity-color-security-groups, #f57c00);
                margin-left: 8px;
            }

            .explorer-button:hover {
                background-color: rgba(245, 124, 0, 0.1);
            }

            .close-button {
                margin-left: 4px;
            }

            .dialog-content {
                padding: 16px 24px;
                min-height: 400px;
                max-height: 70vh;
                overflow-y: auto;
            }

            .security-groups-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                gap: 16px;
            }

            .security-group-card {
                cursor: pointer;
                transition:
                    transform 0.2s,
                    box-shadow 0.2s,
                    border-color 0.2s;
                border: 1px solid rgba(0, 0, 0, 0.08);
                display: flex;
                flex-direction: column;
                padding: 12px 16px;
                background-color: #f5f5f5;
            }

            .security-group-card:hover {
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                border-color: rgba(0, 0, 0, 0.25);
            }

            .card-identity {
                display: flex;
                align-items: center;
                gap: 12px;
                margin-bottom: 12px;
            }

            .entity-avatar {
                width: 40px;
                height: 40px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
            }

            .entity-icon {
                color: white;
                font-size: 22px;
                width: 22px;
                height: 22px;
            }

            .identity-text {
                display: flex;
                flex-direction: column;
                overflow: hidden;
                min-width: 0;
                flex: 1;
            }

            .entity-name {
                font-size: 15px;
                font-weight: 600;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .entity-subtitle {
                font-size: 12px;
                color: rgba(0, 0, 0, 0.55);
                display: flex;
                align-items: center;
                gap: 2px;
            }

            .assigned-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;
                color: #4caf50;
            }

            .card-footer {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 8px;
                border-top: 1px solid rgba(0, 0, 0, 0.06);
                padding-top: 8px;
            }

            .badges-row {
                display: flex;
                flex-wrap: wrap;
                gap: 4px;
                min-width: 0;
            }

            .card-actions {
                display: flex;
                flex-shrink: 0;
                align-items: center;
            }

            .card-actions ::ng-deep .mat-mdc-icon-button {
                width: 32px;
                height: 32px;
                padding: 0;
                display: inline-flex;
                align-items: center;
                justify-content: center;
            }

            .card-actions ::ng-deep .mat-mdc-icon-button .mat-icon {
                font-size: 18px;
                width: 18px;
                height: 18px;
            }

            .badge {
                display: inline-flex;
                align-items: center;
                gap: 2px;
                padding: 2px 6px;
                border-radius: 12px;
                font-size: 12px;
                background-color: rgba(0, 0, 0, 0.06);
                color: rgba(0, 0, 0, 0.5);
            }

            .badge-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;
            }

            /* Position group layout */
            .position-group {
                margin-bottom: 24px;
            }

            .group-header {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 12px;
                margin-bottom: 12px;
                border-left: 3px solid;
                background: rgba(0, 0, 0, 0.03);
                border-radius: 0 4px 4px 0;
                font-size: 14px;
                font-weight: 500;
                cursor: pointer;
                user-select: none;
            }

            .group-header:hover {
                background: rgba(0, 0, 0, 0.06);
            }

            .collapse-icon {
                transition: transform 0.2s;
                color: rgba(0, 0, 0, 0.4);
                font-size: 20px;
                width: 20px;
                height: 20px;
            }

            .collapse-icon.collapsed {
                transform: rotate(-90deg);
            }

            .group-name {
                font-weight: 600;
            }

            .group-count {
                color: rgba(0, 0, 0, 0.45);
                font-weight: 400;
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 48px;
                color: rgba(0, 0, 0, 0.54);
            }

            .empty-icon {
                font-size: 64px;
                width: 64px;
                height: 64px;
                margin-bottom: 16px;
            }

            .mr-2 {
                margin-right: 8px;
            }

            /* Dark theme overrides */
            :host-context(.dark-theme) .security-group-card {
                background-color: #383838;
                border-color: rgba(255, 255, 255, 0.1);
            }
            :host-context(.dark-theme) .security-group-card:hover {
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
                border-color: rgba(255, 255, 255, 0.2);
            }
            :host-context(.dark-theme) .entity-subtitle {
                color: rgba(255, 255, 255, 0.55);
            }
            :host-context(.dark-theme) .card-footer {
                border-top-color: rgba(255, 255, 255, 0.08);
            }
            :host-context(.dark-theme) .badge {
                background-color: rgba(255, 255, 255, 0.1);
                color: rgba(255, 255, 255, 0.5);
            }

            :host-context(.dark-theme) .group-header {
                background: rgba(255, 255, 255, 0.04);
            }
            :host-context(.dark-theme) .group-header:hover {
                background: rgba(255, 255, 255, 0.08);
            }
            :host-context(.dark-theme) .collapse-icon {
                color: rgba(255, 255, 255, 0.4);
            }
            :host-context(.dark-theme) .group-count {
                color: rgba(255, 255, 255, 0.45);
            }
            :host-context(.dark-theme) .empty-state {
                color: rgba(255, 255, 255, 0.54);
            }

            /* Phones: full-screen dialog (global .ob-list-dialog) → tighten chrome and
           drop the 280px-min grid to a single full-width column. */
            @media (max-width: 767.98px) {
                .dialog-header {
                    padding: 12px 16px;
                    font-size: 18px;
                }
                .dialog-content {
                    padding: 12px 16px;
                    min-height: 0;
                }
                /* minmax(0,1fr) (not 1fr) removes the implicit min-content floor so the
               card actually shrinks to the viewport instead of overflowing right. */
                .security-groups-grid {
                    grid-template-columns: minmax(0, 1fr);
                    gap: 12px;
                }
                .security-groups-grid > * {
                    min-width: 0;
                    max-width: 100%;
                }
                .card-identity,
                .identity-text {
                    min-width: 0;
                }
                /* Badges take a full row (wrapping internally), actions drop below. */
                .card-footer {
                    flex-wrap: wrap;
                }
                .badges-row {
                    flex: 1 1 100%;
                }
                .card-actions {
                    margin-left: auto;
                }
            }
        `,
    ],
})
export class OrgSecurityGroupsListDialogComponent implements OnInit {
    public entityColors = inject(OrgEntityColorsService);
    public listView = inject(OrgListViewService);
    public allSecurityGroups: SecurityGroupDisplayCard[] = [];
    public filteredSecurityGroups: SecurityGroupDisplayCard[] = [];
    public searchTerm = '';
    public positionSearchTerm = '';
    public matchedPositionNames: string[] = [];

    // Filters
    public assignedFilter = false;
    public unassignedFilter = false;
    public noFeaturesFilter = false;
    public noHatsFilter = false;
    public noUsersFilter = false;
    public postsFilter = false;
    public assignedCount = 0;
    public unassignedCount = 0;
    public noFeaturesCount = 0;
    public noHatsCount = 0;
    public noUsersCount = 0;
    public postsCount = 0;

    // Position grouping
    public groupByPosition = false;
    public positionGroups: { position: string; securityGroups: SecurityGroupDisplayCard[] }[] = [];
    public collapsedPositions = new Set<string>();

    // Shared component config
    public allPositionNames: string[] = [];
    public filterConfig: OrgFilterConfig[] = [];
    public rightFilterConfig: OrgFilterConfig[] = [];

    constructor(
        private dialog: MatDialog,
        public dialogRef: MatDialogRef<OrgSecurityGroupsListDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: SecurityGroupsListDialogData
    ) {}

    public ngOnInit(): void {
        console.log('OrgSecurityGroupsListDialogComponent > ngOnInit > data:', this.data);
        if (this.data?.positionFilter) {
            this.positionSearchTerm = this.data.positionFilter;
        }
        this.loadSecurityGroups();
    }

    private loadSecurityGroups(): void {
        const org = this.data?.organization;
        if (!org) {
            console.warn('No organization data provided');
            return;
        }

        const orgSecurityGroups = org.orgSecurityGroups;
        const orgHats = org.orgHats;

        if (!orgSecurityGroups) {
            console.warn('No security groups in organization');
            return;
        }

        // Get all security group keys
        const sgKeys = orgSecurityGroups.securityGroupsKeys || [];

        console.log('loadSecurityGroups > sgKeys:', sgKeys);

        // Build a map of which hats use which security groups
        const hatsBySgKey = new Map<string, string[]>();
        const usersBySgKey = new Map<string, Set<string>>();

        if (orgHats?.hatsKeys) {
            for (const hatKey of orgHats.hatsKeys) {
                const hat = orgHats[hatKey];
                if (hat && typeof hat === 'object' && 'securityGroupsArray' in hat) {
                    const hatSgArray =
                        (hat as { securityGroupsArray?: Array<string | { key?: string }> }).securityGroupsArray || [];
                    const hatUsers = (hat as { assignedToArray?: string[] }).assignedToArray || [];

                    for (const sg of hatSgArray) {
                        const sgKey = typeof sg === 'string' ? sg : sg?.key;
                        if (sgKey) {
                            // Track hats
                            if (!hatsBySgKey.has(sgKey)) {
                                hatsBySgKey.set(sgKey, []);
                            }
                            const hatsArray = hatsBySgKey.get(sgKey);
                            if (hatsArray) {
                                hatsArray.push(hatKey);
                            }

                            // Track users
                            if (!usersBySgKey.has(sgKey)) {
                                usersBySgKey.set(sgKey, new Set());
                            }
                            const usersSet = usersBySgKey.get(sgKey);
                            if (usersSet) {
                                for (const userId of hatUsers) {
                                    usersSet.add(userId);
                                }
                            }
                        }
                    }
                }
            }
        }

        this.allSecurityGroups = restrictToKeys(sgKeys as string[], this.data?.onlyKeys, (k) => k)
            .map((sgKey: string) => {
                const orgSg = orgSecurityGroups[sgKey] as OrgSecurityGroup;

                if (!orgSg || typeof orgSg !== 'object') {
                    return null;
                }

                const featureCount = orgSg?.features?.length || 0;
                const hatCount = hatsBySgKey.get(sgKey)?.length || 0;
                const userCount = usersBySgKey.get(sgKey)?.size || 0;

                return {
                    key: sgKey,
                    name: orgSg?.name || sgKey,
                    featureCount,
                    hatCount,
                    userCount,
                    isAssigned: orgSg?.assigned || false,
                    positionNames: this.resolvePositionNamesForSg(hatsBySgKey.get(sgKey) || [], orgHats, org),
                    featureNames: this.resolveFeatureNames(orgSg, org),
                    hatNames: this.resolveHatNames(hatsBySgKey.get(sgKey) || [], orgHats),
                    userNames: this.resolveUserNames(usersBySgKey.get(sgKey) || new Set(), org),
                    isOnPost: this.isSgLinkedToPost(hatsBySgKey.get(sgKey) || [], orgHats, org),
                    orgSecurityGroup: orgSg,
                } as SecurityGroupDisplayCard;
            })
            .filter((sg): sg is SecurityGroupDisplayCard => sg !== null);

        // Sort by name
        this.allSecurityGroups.sort((a, b) => a.name.localeCompare(b.name));
        this.computeFilterCounts();
        this.allPositionNames = Array.from(new Set(this.allSecurityGroups.flatMap((sg) => sg.positionNames))).sort();
        this.buildFilterConfigs();
        this.filterSecurityGroups();

        console.log('loadSecurityGroups > allSecurityGroups:', this.allSecurityGroups);
    }

    private computeFilterCounts(): void {
        this.assignedCount = this.allSecurityGroups.filter((sg) => sg.isAssigned).length;
        this.unassignedCount = this.allSecurityGroups.filter((sg) => !sg.isAssigned).length;
        this.noFeaturesCount = this.allSecurityGroups.filter((sg) => sg.featureCount === 0).length;
        this.noHatsCount = this.allSecurityGroups.filter((sg) => sg.hatCount === 0).length;
        this.noUsersCount = this.allSecurityGroups.filter((sg) => sg.userCount === 0).length;
        this.postsCount = this.allSecurityGroups.filter((sg) => sg.isOnPost).length;
        this.buildFilterConfigs();
    }

    public get hasActiveFilter(): boolean {
        return (
            this.assignedFilter ||
            this.unassignedFilter ||
            this.noFeaturesFilter ||
            this.noHatsFilter ||
            this.noUsersFilter ||
            this.postsFilter
        );
    }

    public filterSecurityGroups(): void {
        let result = [...this.allSecurityGroups];

        // Text search across name, features, hats, users, positions
        if (this.searchTerm.trim()) {
            const term = this.searchTerm.toLowerCase().trim();
            result = result.filter(
                (sg) =>
                    sg.name.toLowerCase().includes(term) ||
                    sg.featureNames.some((f) => f.toLowerCase().includes(term)) ||
                    sg.hatNames.some((h) => h.toLowerCase().includes(term)) ||
                    sg.userNames.some((u) => u.toLowerCase().includes(term)) ||
                    sg.positionNames.some((p) => p.toLowerCase().includes(term))
            );
        }

        // Position search - filter SGs linked to matching positions through hats (fuzzy)
        if (this.positionSearchTerm.trim()) {
            result = result.filter((sg) => sg.positionNames.some((pos) => fuzzyMatch(pos, this.positionSearchTerm)));
        }

        // Toggle filters
        if (this.assignedFilter) {
            result = result.filter((sg) => sg.isAssigned);
        }
        if (this.unassignedFilter) {
            result = result.filter((sg) => !sg.isAssigned);
        }
        if (this.noFeaturesFilter) {
            result = result.filter((sg) => sg.featureCount === 0);
        }
        if (this.noHatsFilter) {
            result = result.filter((sg) => sg.hatCount === 0);
        }
        if (this.noUsersFilter) {
            result = result.filter((sg) => sg.userCount === 0);
        }
        if (this.postsFilter) {
            result = result.filter((sg) => sg.isOnPost);
        }

        this.filteredSecurityGroups = result;

        if (this.groupByPosition) {
            this.buildPositionGroups();
        }
    }

    public onSearchChanged(term: string): void {
        this.searchTerm = term;
        this.filterSecurityGroups();
    }

    public onPositionSearchChanged(result: PositionSearchResult): void {
        this.positionSearchTerm = result.term;
        this.matchedPositionNames = result.matchedNames;
        this.filterSecurityGroups();
    }

    public onFiltersChanged(activeFilters: Set<string>): void {
        this.assignedFilter = activeFilters.has('assigned');
        this.unassignedFilter = activeFilters.has('unassigned');
        this.noFeaturesFilter = activeFilters.has('noFeatures');
        this.noHatsFilter = activeFilters.has('noHats');
        this.noUsersFilter = activeFilters.has('noUsers');
        this.postsFilter = activeFilters.has('posts');

        const newGroupByPosition = activeFilters.has('groupByPosition');
        if (newGroupByPosition !== this.groupByPosition) {
            this.groupByPosition = newGroupByPosition;
            if (!this.groupByPosition) {
                this.collapsedPositions.clear();
            }
        }

        this.filterSecurityGroups();
    }

    private buildFilterConfigs(): void {
        this.filterConfig = [
            { key: 'assigned', label: 'Assigned', icon: 'check_circle', count: this.assignedCount },
            { key: 'unassigned', label: 'Unassigned', icon: 'unpublished', count: this.unassignedCount },
            { key: 'noFeatures', label: 'No Features', icon: 'star_border', count: this.noFeaturesCount },
            { key: 'noHats', label: 'No Hats', icon: 'school', count: this.noHatsCount },
            { key: 'noUsers', label: 'No Staff', icon: 'person_off', count: this.noUsersCount },
            { key: 'posts', label: 'Posts', icon: 'work', count: this.postsCount },
        ];
        this.rightFilterConfig = [{ key: 'groupByPosition', label: 'By Org Part', icon: 'account_tree' }];
    }

    public togglePositionCollapse(position: string): void {
        if (this.collapsedPositions.has(position)) {
            this.collapsedPositions.delete(position);
        } else {
            this.collapsedPositions.add(position);
        }
    }

    private buildPositionGroups(): void {
        const groupMap = new Map<string, SecurityGroupDisplayCard[]>();

        this.filteredSecurityGroups.forEach((sg) => {
            if (sg.positionNames.length === 0) {
                const key = 'No Org Part';
                if (!groupMap.has(key)) groupMap.set(key, []);
                groupMap.get(key)?.push(sg);
            } else {
                sg.positionNames.forEach((pos) => {
                    if (!groupMap.has(pos)) groupMap.set(pos, []);
                    groupMap.get(pos)?.push(sg);
                });
            }
        });

        this.positionGroups = Array.from(groupMap.entries())
            .map(([position, securityGroups]) => ({ position, securityGroups }))
            .sort((a, b) => {
                if (a.position === 'No Org Part') return 1;
                if (b.position === 'No Org Part') return -1;
                return a.position.localeCompare(b.position);
            });

        // Collapse all by default on first build
        if (this.collapsedPositions.size === 0 && this.positionGroups.length > 0) {
            this.positionGroups.forEach((g) => this.collapsedPositions.add(g.position));
        }
    }

    private isSgLinkedToPost(hatKeys: string[], orgHats: Record<string, unknown>, org: Organization): boolean {
        const orgParts = org.orgParts || [];
        return hatKeys.some((hatKey) => {
            const hatRaw = orgHats?.[hatKey];
            if (!hatRaw || typeof hatRaw !== 'object') return false;
            const hat = hatRaw as Record<string, unknown>;
            const positionArray = (hat['positionArray'] || []) as string[];
            return positionArray.some((partKey: string) => {
                const part = orgParts.find((p) => p.key === partKey || p.partKey === partKey);
                if (!part) return false;
                const partType = part.partType ? part.partType.toLowerCase() : '';
                if (partType === 'post') return true;
                if (!partType && (part.depth || 0) >= 6) return true;
                return false;
            });
        });
    }

    private resolvePositionNamesForSg(
        hatKeys: string[],
        orgHats: Record<string, unknown>,
        org: Organization
    ): string[] {
        const names: string[] = [];
        const orgParts = org.orgParts || [];

        for (const hatKey of hatKeys) {
            const hatRaw = orgHats?.[hatKey];
            if (!hatRaw || typeof hatRaw !== 'object') continue;
            const hat = hatRaw as Record<string, unknown>;

            // Try positions array (objects with name)
            if (hat['positions'] && Array.isArray(hat['positions'])) {
                for (const pos of hat['positions'] as Array<{ name?: string }>) {
                    if (pos?.name && !names.includes(pos.name)) {
                        names.push(pos.name);
                    }
                }
            }

            // Try positionArray (keys) - resolve names from org parts
            if (hat['positionArray'] && Array.isArray(hat['positionArray'])) {
                for (const partKey of hat['positionArray'] as string[]) {
                    const part = orgParts.find((p) => p.key === partKey || p.partKey === partKey);
                    if (part?.name && !names.includes(part.name)) {
                        names.push(part.name);
                    }
                }
            }

            // Try orgPosition
            if (hat['orgPosition']) {
                const orgPos = hat['orgPosition'] as Record<string, unknown>;
                const posKey = (orgPos['key'] || orgPos['partKey']) as string;
                if (posKey) {
                    const part = orgParts.find((p) => p.key === posKey || p.partKey === posKey);
                    if (part?.name && !names.includes(part.name)) {
                        names.push(part.name);
                    }
                }
            }
        }

        return names;
    }

    private resolveFeatureNames(orgSg: OrgSecurityGroup, org: Organization): string[] {
        const featureKeys = orgSg?.features || [];
        const orgFeatures = (org as any)?.orgFeatures || {};
        return featureKeys
            .map((fKey: string) => orgFeatures[fKey]?.name || fKey)
            .filter((name: string) => name !== 'systemFeature');
    }

    private resolveHatNames(hatKeys: string[], orgHats: Record<string, unknown>): string[] {
        return hatKeys
            .map((k) => {
                const hat = orgHats?.[k];
                return hat && typeof hat === 'object' ? (hat as any).name || '' : '';
            })
            .filter((n) => !!n);
    }

    private resolveUserNames(userKeys: Set<string>, org: Organization): string[] {
        const orgUsers = org?.orgUsers;
        if (!orgUsers) return [];
        return Array.from(userKeys)
            .filter((k) => orgUsers[k])
            .map((k) => orgUsers[k].name || orgUsers[k].userName || '');
    }

    public onSecurityGroupDoubleClick(sg: SecurityGroupDisplayCard): void {
        this.openSecurityGroupDetails(sg);
    }

    public onViewSecurityGroup(sg: SecurityGroupDisplayCard): void {
        this.openSecurityGroupDetails(sg);
    }

    public onEditSecurityGroup(sg: SecurityGroupDisplayCard): void {
        // TODO: Implement edit functionality
        console.log('Edit security group:', sg.key);
    }

    private openSecurityGroupDetails(sg: SecurityGroupDisplayCard): void {
        console.log('Opening security group details for:', sg.name);

        this.dialog.open(OrgSecurityGroupDetailsDialogComponent, {
            width: '85vw',
            height: '85vh',
            maxWidth: '1400px',
            maxHeight: '900px',
            data: {
                securityGroup: sg.orgSecurityGroup,
                securityGroupKey: sg.key,
                organization: this.data.organization,
            },
            panelClass: 'security-group-details-dialog',
        });
    }

    public openSecurityGroupExplorer(): void {
        import('../org-security-group-explorer-dialog/org-security-group-explorer-dialog').then((m) => {
            this.dialog.open(m.OrgSecurityGroupExplorerDialogComponent, {
                width: '90%',
                height: '90vh',
                maxWidth: '1400px',
                maxHeight: '900px',
                data: {
                    organization: this.data.organization,
                },
                panelClass: ['security-group-explorer-dialog', 'ob-list-dialog'],
            });
        });
    }
}
